import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import fumisLogo from 'src/assets/Fumis-logo-with-leaf.svg';
import PhoneInput from 'src/components/ui/PhoneInput/PhoneInput';
import { ThemeSwitch } from 'src/components/ui/ThemeSwitch/ThemeSwitch';
import { changeSettings, getSupportInfo } from 'src/store/actions/authActions';
import { useFileSelector } from 'src/utils/useFileSelector';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { addModal } from '../../../../features/globalSlice';
import { ModalType } from '../../../../store/models/Modal';
import countries from '../../../../utils/countries';
import Avatar from '../../../ui/Avatar/Avatar';
import Button from '../../../ui/Button/Button';
import Input from '../../../ui/Input/Input';
import PageHeader from '../../../ui/PageHeader/PageHeader';
import UploadPicture from '../../../ui/UploadPicture/UploadPicture';
import classes from './ManufacturerSettings.module.scss';

const ManufacturerSettings: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const imageId = useRef(uuidv4());

  const image = useFileSelector({ id: imageId.current })?.[0];
  const user = useAppSelector((state) => state.auth.user);
  const supportInfo = useAppSelector((state) => state.auth.supportInfo);

  useEffect(() => {
    dispatch(getSupportInfo());
    // eslint-disable-next-line
  }, []);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:settings')}
        endNode={
          <div className={classes.HeaderRight}>
            <ThemeSwitch />
            <Button
              disabled={!image}
              onClick={() => dispatch(changeSettings({ image: image }))}
              className={classes.HeaderButton}
            >
              {t('common:save_changes')}
            </Button>
          </div>
        }
      />
      <h4>{t('settings:company_logo')}</h4>
      <UploadPicture
        imageComponent={
          <Avatar
            image={image ? image?.url : user?.image}
            imageSize={128}
            showTitles={false}
            title={user?.manufacturer?.baseCompany?.title}
          />
        }
        imageId={imageId.current}
        enableRemove={!!(image?.url || (user?.image && image?.url !== null))}
        className={classes.Picture}
      />
      <h4>{t('service_account:company_info')}</h4>
      <p>{t('settings:change_company_info_warning')}</p>
      <section>
        <Input
          value={user?.manufacturer?.baseCompany?.title}
          disabled
          label={t('common:name')}
        />
        <div />
        <div />
        <Input
          value={user?.manufacturer?.baseCompany?.line1}
          disabled
          label={t('common:address_line_1')}
        />
        <Input
          value={user?.manufacturer?.baseCompany?.line2}
          disabled
          label={`${t('common:address_line_2')} (${t(
            'common:optional'
          ).toLowerCase()})`}
        />
        <div />
        <Input
          value={user?.manufacturer?.baseCompany?.zip}
          disabled
          label={t('common:zip_postcode')}
        />
        <Input
          value={user?.manufacturer?.baseCompany?.city}
          disabled
          label={t('common:city')}
        />
        <Input
          disabled
          label={t('common:country')}
          select
          value={user?.manufacturer?.baseCompany?.country}
        >
          {Object.keys(countries?.getNames('en'))?.map((v, i) => (
            <option key={i} value={v}>
              {countries?.getNames('en')?.[v]}
            </option>
          ))}
        </Input>

        <Input
          value={user?.manufacturer?.baseCompany?.eMail}
          disabled
          label={t('common:email')}
        />
        <PhoneInput
          value={user?.manufacturer?.baseCompany?.phone}
          disabled
          label={t('common:phone')}
        />
        <Input
          value={user?.manufacturer?.baseCompany?.website}
          disabled
          label={t('common:website')}
        />
        <Button
          size="large"
          variant="outlined"
          onClick={() =>
            dispatch(
              addModal({
                id: '1',
                title: t('settings:change_password'),
                modalType: ModalType.CHANGE_PASSWORD,
                primaryButtonText: t('common:confirm'),
                secondaryButtonText: t('common:cancel'),
                onBackdropClose: false,
              })
            )
          }
        >
          {t('settings:change_password')}
        </Button>
      </section>
      <h4>{t('settings:contact_person')}</h4>
      <p>{t('settings:contact_person_warning')}</p>
      <section>
        <Input
          value={user?.manufacturer?.contactPerson}
          disabled
          label={t('common:name')}
        />
        <Input
          value={user?.manufacturer?.eMail}
          disabled
          label={t('common:email')}
        />
        <PhoneInput
          value={user?.manufacturer?.phone}
          disabled
          label={t('common:phone')}
        />
      </section>
      <h4>{t('common:support')}</h4>
      <div className={classes.SupportInfo}>
        <Avatar
          imageSize={96}
          showTitles={false}
          image={fumisLogo}
          className={classes.Logo}
        />
        <p>
          {`${t('common:email')}: `}
          <a href={`mailto: ${supportInfo?.eMail}`}>{supportInfo?.eMail}</a>
        </p>
        <p>
          {`${t('common:phone')}: `}
          <a href={`tel: ${supportInfo?.phone}`}>{supportInfo?.phone}</a>
        </p>
        <p>
          {`${t('common:website')}: `}
          <a href={supportInfo?.website}>{supportInfo?.website}</a>
        </p>
      </div>
    </div>
  );
};

export default ManufacturerSettings;
